import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import * as Yup from 'yup';

import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    Select,
    TextField,
    Tooltip,
} from '@material-ui/core';
import { FormattedMessage, injectIntl } from 'react-intl';

import DialogComponent from 'app/components/DialogComponent';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import { KeyboardDatePicker } from '@material-ui/pickers';
import FileField from 'app/components/FileField';
import certificationEndpoints from 'app/api/services/certificationEndpoints';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import DeleteIcon from '@material-ui/icons/Delete';
import companyEndpoints from 'app/api/services/companyEndpoints';

import { errorHandler } from 'app/utils/errorHandler';

import notification from 'app/config/notification';
import Alert from '@material-ui/lab/Alert';

function Modal(props) {
    const [changingDate, setChangingDate] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [downloadingCrc, setDownloadingCrc] = useState(false);
    const [sending, setSending] = useState(false);
    const [vehiclesAvailable, setVehiclesAvailable] = useState([]);
    const [vehicle, setVehicle] = useState(null);
    const [company, setCompany] = useState({});

    const {
        classes,
        open,
        onClose,
        certification,
        request,
        requestJsonData,
        onSave,
        downloadFile,
        updateFile,
        setUpdateFile,
        vehicles,
        usedVehicles,
    } = props;

    const certNumber = useMemo(() => {
        if (certification?.numeroVistoria) {
            return String(certification.numeroVistoria).padStart(9, '0');
        }
        if (certification?.cdCertificado) {
            return String(certification.cdCertificado).padStart(3, '0');
        }
        return '';
    }, [certification]);

    const vehicleOpened = useMemo(() => {
        if (certification?.cdVeiculo) {
            const found = vehicles.filter((v) => v.cdVeiculo === certification.cdVeiculo);
            if (found) {
                setVehicle(found[0]);
                return found[0];
            }
        }
        return null;
    }, [certification, vehicles]);

    const certificationInitialValue = useMemo(() => {
        if (certification && certification.cdVeiculo !== null) {
            return certification;
        }
        return {
            cdVeiculo: null,
            deEmpresa: '',
            dtEmissaoCrc: null,
            dtVencimentoCrc: null,
            deObservacao: '',
        };
    }, [certification]);

    useEffect(() => {
        const init = async () => {
            if (open) {
                if (vehicles) {
                    let temp = _.cloneDeep(vehicles);
                    temp = temp.filter((vv) => vv.cdVeiculo && !usedVehicles.includes(vv.cdVeiculo));
                    setVehiclesAvailable(temp);
                }
                if (request.cdEmpresa) {
                    setLoadingCompany(true);
                    const response = await companyEndpoints.getCompany({ cdEmpresa: request.cdEmpresa });
                    if (response?.status === 200 && response?.data) {
                        setCompany(response.data);
                    }
                    setLoadingCompany(false);
                }
            } else {
                setVehicle(null);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onChangeDate = async (values, field, date, vehi, dataVistoria) => {
        setChangingDate(changingDate + 1);
        values[field] = date;

        if (field === 'dtEmissaoCrc') {
            await changingExpirationDate(dataVistoria, values, vehi);
        }
    };

    const changingExpirationDate = async (value, values, vehi) => {
        if (moment.utc(value).isValid()) {
            setLoading(true);
            const date = await searchExpirationDate(value, vehi);
            await onChangeDate(values, 'dtVencimentoCrc', date);
            setLoading(false);
        } else {
            await onChangeDate(values, 'dtVencimentoCrc', null);
        }
    };

    const searchExpirationDate = async (createDate, vehi) => {
        try {
            if (vehicle || vehi || vehicleOpened) {
                const response = await certificationEndpoints.getExpirationDate({
                    cdSolicitacao: request.cdSolicitacao,
                    cdTipoEmpresa: Number(requestJsonData.cdTipoEmpresa),
                    dtEmissaoCrc: moment.utc(createDate).format('YYYY-MM-DD'),
                    numeroCrc: certNumber ? certNumber : null,
                    cdVeiculo: vehi || vehicle?.cdVeiculo || vehicle || vehicleOpened?.cdVeiculo,
                });
                if (response.status === 200 && response.data) {
                    return moment.utc(response.data);
                }
            } else {
                notification('Selecione o veículo', {
                    toastId: 'SELECT_VEHICLE',
                    type: 'warning',
                });
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        return null;
    };

    const onCancel = () => {
        setUpdateFile(false);
        onClose();
    };

    const deleteFile = (values) => {
        setUpdateFile(true);
        values.updateFile = true;
    };

    const onChangeVehicle = async (e, values) => {
        const value = e.target.value === '' ? null : Number(e.target.value);
        const found = vehicles.find((v) => v.cdVeiculo === value && !v.boVeiculoNovo);
        const dataVistoria = value === null ? null : found?.dtVistoria || new Date();
        values['cdVeiculo'] = value ? value : null;
        setVehicle(value);
        await onChangeDate(values, 'dtEmissaoCrc', new Date(), value, dataVistoria);
    };

    const handleSave = async (values) => {
        if (values.idFileCrc && !updateFile) {
            notification('Para alterar dados do certificado, é necessário remover o certificado atual.', {
                toastId: 'DELETE_FILE_NEEDED',
                type: 'warning',
            });
            return;
        }
        setSending(true);
        if (!values.dtVencimentoCrc || values.dtVencimentoCrc === '') {
            values.dtVencimentoCrc = await searchExpirationDate(values);
        }
        if (!vehicle) {
            notification('Selecione o veículo', {
                toastId: 'SELECT_VEHICLE',
                type: 'warning',
            });
            setSending(false);
            return;
        }

        if (vehicleOpened) {
            values.cdVeiculo = Number(vehicleOpened.cdVeiculo);
        } else {
            values.cdVeiculo = Number(vehicle);
        }

        await onSave(values);
        setSending(false);
        setVehicle(null);
    };

    const validationSchema = Yup.object().shape({
        cdVeiculo: Yup.string().required('Campo obrigatório').nullable(),
        dtEmissaoCrc: Yup.date().required('Campo obrigatório').typeError('Data inválida').nullable(),
        dtVencimentoCrc: Yup.date()
            .required('Campo obrigatório')
            .typeError('Data inválida')
            .nullable()
            .test('is-after', 'A data de vencimento deve ser maior que a data de emissão', function (value) {
                const { dtEmissaoCrc } = this.parent;
                return (
                    !value || !dtEmissaoCrc || moment(value).startOf('day').isAfter(moment(dtEmissaoCrc).startOf('day'))
                );
            }),
    });

    return (
        <DialogComponent title={'Detalhamento do certificado'} open={open} onClose={onClose}>
            <Formik
                initialValues={certificationInitialValue}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    handleSave(values);
                }}>
                {({ values, errors, validateForm }) => {
                    return (
                        <Form className={classes.containerForm}>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Número</InputLabel>
                                    <Field
                                        name=""
                                        disabled
                                        variant="filled"
                                        margin="dense"
                                        value={certNumber}
                                        fullWidth
                                        as={TextField}
                                    />
                                </Grid>
                                {vehicleOpened && (
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel>Placa</InputLabel>
                                        <Field
                                            name=""
                                            disabled
                                            variant="filled"
                                            margin="dense"
                                            value={vehicleOpened.veiculo?.dePlaca}
                                            fullWidth
                                            as={TextField}
                                        />
                                    </Grid>
                                )}
                                {!vehicleOpened && (
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel>Placa</InputLabel>
                                        <FormControl variant="filled" margin="dense" fullWidth>
                                            <Field
                                                name="cdVeiculo"
                                                id="cdVeiculo"
                                                native
                                                as={Select}
                                                value={!vehicle ? '' : vehicle.cdVeiculo ? vehicle.cdVeiculo : vehicle}
                                                onChange={(e) => onChangeVehicle(e, values)}
                                                disabled={loading}
                                                error={!!errors.cdVeiculo}>
                                                <option aria-label="None" value="">
                                                    Selecione
                                                </option>
                                                {vehiclesAvailable.map((v) => {
                                                    return (
                                                        <option key={`ve-${v.cdVeiculo}`} value={v.cdVeiculo}>
                                                            {v.veiculo?.dePlaca}
                                                        </option>
                                                    );
                                                })}
                                            </Field>
                                            {!!errors?.cdVeiculo && (
                                                <FormHelperText error={!!errors?.cdVeiculo}>
                                                    {errors.cdVeiculo}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Empresa</InputLabel>
                                    {loadingCompany && (
                                        <div className={classes.progress}>
                                            <CircularProgress size={20} />
                                        </div>
                                    )}
                                    {!loadingCompany && (
                                        <Field
                                            name="deEmpresa"
                                            disabled
                                            variant="filled"
                                            margin="dense"
                                            fullWidth
                                            value={
                                                company.cdTipoPessoaFisica ? company.nmCompleto : company.deRazaoSocial
                                            }
                                            as={TextField}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Emissão</InputLabel>
                                    <Field
                                        name="dtEmissaoCrc"
                                        disabled={loading || !values.cdVeiculo || (values.idFileCrc && !updateFile)}
                                        disableToolbar
                                        format="DD/MM/YYYY"
                                        emptyLabel={''}
                                        autoOk
                                        clearable
                                        clearLabel={'Limpar'}
                                        value={values.dtEmissaoCrc || null}
                                        invalidLabel="Data inválida"
                                        invalidDateMessage="Data inválida"
                                        maxDateMessage="Data inválida"
                                        minDateMessage="Data inválida"
                                        margin="dense"
                                        onChange={(v) => onChangeDate(values, 'dtEmissaoCrc', v)}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        inputVariant="filled"
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        as={KeyboardDatePicker}
                                        error={!!errors.dtEmissaoCrc}
                                        helperText={errors.dtEmissaoCrc ? errors.dtEmissaoCrc : ''}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Vencimento</InputLabel>
                                    {loading && (
                                        <div className={classes.progress}>
                                            <CircularProgress size={20} />
                                        </div>
                                    )}
                                    {!loading && (
                                        <Field
                                            name="dtVencimentoCrc"
                                            disabled={!values.dtEmissaoCrc || (values.idFileCrc && !updateFile)}
                                            disableToolbar
                                            format="DD/MM/YYYY"
                                            emptyLabel={''}
                                            autoOk
                                            clearable
                                            clearLabel={'Limpar'}
                                            value={values.dtVencimentoCrc || null}
                                            invalidLabel="Data inválida"
                                            invalidDateMessage="Data inválida"
                                            maxDateMessage="Data inválida"
                                            minDateMessage="Data inválida"
                                            margin="dense"
                                            onChange={(v) => onChangeDate(values, 'dtVencimentoCrc', v)}
                                            KeyboardButtonProps={{
                                                'aria-label': 'change date',
                                            }}
                                            inputVariant="filled"
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            as={KeyboardDatePicker}
                                            error={!!errors.dtVencimentoCrc}
                                            helperText={errors.dtVencimentoCrc ? errors.dtVencimentoCrc : ''}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel>Observações</InputLabel>
                                    <Field
                                        name="deObservacao"
                                        disabled={values.idFileCrc && !updateFile}
                                        variant="filled"
                                        margin="dense"
                                        multiline
                                        value={values.deObservacao ? values.deObservacao : ''}
                                        minRows={4}
                                        fullWidth
                                        as={TextField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                {values.idFileCrc && !updateFile && (
                                    <>
                                        <Grid item xs={12} className={classes.containerIcons}>
                                            <Tooltip title="Excluir arquivo atual e gerar novo">
                                                <IconButton
                                                    className={classes.btnDeleteFile}
                                                    size="small"
                                                    onClick={() => deleteFile(values)}>
                                                    <DeleteIcon className={classes.iconDelete} />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                color="primary"
                                                className={classes.btnActualFile}
                                                component="span"
                                                disableElevation
                                                disabled={downloadingCrc}
                                                onClick={async () => {
                                                    setDownloadingCrc(true);
                                                    await downloadFile(values.idFileCrc);
                                                    setDownloadingCrc(false);
                                                }}>
                                                {downloadingCrc ? (
                                                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                                                ) : (
                                                    <InsertDriveFileIcon className={classes.icon} fontSize="inherit" />
                                                )}
                                                Ver arquivo atual
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Alert severity="warning">
                                                Atenção: Para alterar dados do certificado, é necessário remover o
                                                certificado atual.
                                            </Alert>
                                        </Grid>
                                    </>
                                )}
                                {(!values.idFileCrc || updateFile) && (
                                    <Grid item xs={12} sm={6} md={6}>
                                        <Field
                                            value={{}}
                                            required={true}
                                            name={`doc-1`}
                                            label={'Carregar arquivo'}
                                            id={`doc-1`}
                                            component={FileField}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container className={classes.actionsContainer}>
                                {sending && (
                                    <Grid item xs={12} className={classes.centerProgress}>
                                        <CircularProgress size={20} />
                                    </Grid>
                                )}
                                {!sending && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <Button className={classes.actionsBtns} onClick={() => onCancel()}>
                                                <FormattedMessage id="LABELS.COMMONS.CANCEL" />
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={classes.textCenter}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={classes.actionsBtns}
                                                disabled={loading}
                                                onClick={() => {
                                                    validateForm().then(async (res) => {
                                                        if (Object.keys(res).length <= 0) {
                                                            handleSave(values);
                                                        } else {
                                                            notification(
                                                                'O(s) campo(s) sinalizado(s) precisam de revisão.',
                                                                {
                                                                    toastId: 'VALIDATION',
                                                                    type: 'error',
                                                                },
                                                            );
                                                        }
                                                    });
                                                }}>
                                                <Box className={classes.colorWhite}>Salvar / Gerar </Box>
                                            </Button>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Form>
                    );
                }}
            </Formik>
        </DialogComponent>
    );
}

export default injectIntl(Modal);
